import { useEffect, useRef } from 'react'
import { trackPageView } from '@/services/appInsights'
import { useContentBundle } from '@/hooks/useContentBundle'
import { useAppStore } from '@/store/appStore'

// appendices and overview are tracked per sub-page via ContentNavigator's onItemSelect
type TrackedOverlayType = 'release-notes' | 'faq' | 'glossary'

const TRACKED: Set<string> = new Set(['release-notes', 'faq', 'glossary'])

const OVERLAY_NAMES: Record<TrackedOverlayType, string> = {
  'release-notes': 'Release Notes',
  faq: 'FAQ',
  glossary: 'Glossary',
}

export function useOverlayPageTracking(): void {
  const { overlayPage, isManualOffline, isTrulyOffline, offlineSessionId } = useAppStore()
  const { data: bundle } = useContentBundle()

  // Refs keep latest values without making them effect dependencies
  const bundleRef = useRef(bundle)
  bundleRef.current = bundle
  const offlineRef = useRef({ isManualOffline, isTrulyOffline, offlineSessionId })
  offlineRef.current = { isManualOffline, isTrulyOffline, offlineSessionId }

  useEffect(() => {
    if (!overlayPage || !TRACKED.has(overlayPage.type)) return

    const type = overlayPage.type as TrackedOverlayType
    const name = OVERLAY_NAMES[type]

    const { isManualOffline: manual, isTrulyOffline: network, offlineSessionId: sessionId } = offlineRef.current
    const offlineMode = manual && network ? 'both' : manual ? 'manual' : network ? 'network' : 'none'
    const isOffline = manual || network

    const properties: Record<string, string> = { offlineMode }
    if (isOffline && sessionId) properties.offlineSessionId = sessionId

    if (type === 'glossary') {
      const termMatch = overlayPage.hash?.match(/^#term-(\d+)$/)
      const termId = termMatch ? Number(termMatch[1]) : undefined
      const term = termId !== undefined
        ? bundleRef.current?.glossaryTerms.find((g) => g.id === termId)
        : undefined
      const fakeUrl = termId !== undefined ? `/glossary/${termId}` : '/glossary'
      if (termId !== undefined) properties.glossaryTermId = String(termId)
      if (term?.term) properties.glossaryTermName = term.term
      trackPageView(name, fakeUrl, properties)
    } else {
      const idMatch = overlayPage.hash?.match(/^#reading-(\d+)$/)
      const itemId = idMatch ? Number(idMatch[1]) : undefined
      const item = itemId !== undefined
        ? bundleRef.current?.additionalReading.find((r) => r.id === itemId)
        : undefined
      const fakeUrl = itemId !== undefined ? `/${type}/${itemId}` : `/${type}`
      if (itemId !== undefined) properties.additionalReadingId = String(itemId)
      if (item?.displayName) properties.additionalReadingName = item.displayName
      trackPageView(name, fakeUrl, properties)
    }
  }, [overlayPage])
}
